import { useParams } from 'react-router-dom';
import { toastSuccess, toastError } from '../utils/toasts';
import { useGetOrderReferralDataQuery } from '../store';
import LoadingLottie from '../components/atoms/LoadingLottie';
import TitleTextCard from '../components/atoms/TitleTextCard';
import Button from '../components/atoms/Button';
import getOrderOwnerReferralLink from '../utils/getOrderOwnerReferralLink';
import OrderReferralProgressBar from '../components/OrderReferralProgressBar';
import { formatCurrency } from '../utils/shared';
import Input from '../components/atoms/Input';
import CardsLottie from '../components/CardsLottie';

const OrderReferralPage: React.FC = () => {
  const { order_id: orderId } = useParams();

  const { data, isLoading } = useGetOrderReferralDataQuery(
    { orderId: orderId || '' },
    { skip: !orderId },
  );

  const order = data?.order;

  const handleCopyReferralLink = () => {
    if (!order) {
      return;
    }

    const link = getOrderOwnerReferralLink(order.event, order);
    if (!link) {
      return;
    }
    navigator.clipboard
      .writeText(link)
      .then(() => toastSuccess('Link copiado al portapapeles'))
      .catch(() => toastError('Error al copiar el link'));
  };

  if (isLoading) {
    return <LoadingLottie />;
  }

  if (!order) {
    return (
      <div className="flex flex-col gap-6 my-6">
        <div className="glass-surface">
          <h2 className="text-lg font-semibold text-text-primary">No encontramos la información de tu referencia</h2>
          <p className="text-text-primary">Verificá el link e intentá nuevamente.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 my-6 min-h-[calc(100vh-300px)]">
      <CardsLottie />
      <div className="flex flex-col gap-4 mt-4">
        <h1 className="text-[24px] md:text-[32px] font-semibold text-text-primary text-center">¡Hola, {order.items?.[0].name}!</h1>
        <p className="text-center text-text-primary">Este es tu panel de referidos de <b>{order.event?.name}</b></p>
      </div>

      <TitleTextCard title={data?.progress.reimburse_amount > 0 ? `¡Ya recuperaste ${formatCurrency(data?.progress.reimburse_amount)}! 🎉🎉` : '¡Empezá a compartir tu link y recuperá el dinero de tu entrada!'} additionalCardClasses="h-full">
        <p className="text-text-primary">{data?.progress.reimburse_amount > 0 ? `¡Ya recuperaste ${formatCurrency(data?.progress.reimburse_amount)}! Segui compartiendo y recupera hasta ${formatCurrency(data?.progress.limit_amount)}` : `Compartí tu link y recuperá hasta ${formatCurrency(data?.progress.limit_amount)}`}</p>
        <div className="flex flex-col gap-4 mt-6">
          <div className="flex items-baseline justify-between">
            <span className="text-text-primary text-lg">Progreso</span>
            <span className="text-text-primary font-medium">{data?.order.owner_referral?.tickets_sold} / 10 entradas</span>
          </div>
          <OrderReferralProgressBar ticketsSold={data?.order.owner_referral?.tickets_sold || 0} showTicketIcon />
        </div>
      </TitleTextCard>
      <div className='flex flex-col md:flex-row gap-4'>

        <TitleTextCard title="Ventas por link" additionalCardClasses="h-full">
          <div className="grid grid-cols-2 text-center mb-3">
            <div className="flex flex-col">
              <span className="text-text-primary text-sm">Veces usado</span>
              <span className="text-text-primary text-xl font-semibold">{data?.orders_by_referral.length || 0}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-text-primary text-sm">Entradas vendidas</span>
              <span className="text-text-primary text-xl font-semibold">{data?.order.owner_referral?.tickets_sold || 0}</span>
            </div>
          </div>
          <div>
            {data?.orders_by_referral.length === 0 ? (
              <p className="text-text-primary">Aún no hay compras con tu link.</p>
            ) : (
              <div className="flex flex-col divide-y divide-border-tertiary border-t border-border-tertiary border-b">
                {data?.orders_by_referral.map((r, idx) => (
                  <div key={`${r.buyer_name}-${idx}`} className="flex items-center justify-between py-3">
                    <div className="flex flex-col px-4">
                      <span className="text-text-primary">{r.buyer_name}</span>
                    </div>
                    <div className="text-text-primary font-medium px-4">{r.items_count} entradas</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TitleTextCard>

        <TitleTextCard title="Tu link" additionalCardClasses="h-full py-4">
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <Input
              id="referral-link"
              label="Tu link"
              type="text"
              value={getOrderOwnerReferralLink(order.event, order) || ''}
              readOnly
            />
            <div className="w-full md:max-w-xs">
              <Button variant="primary" onClick={handleCopyReferralLink}>Copiar link</Button>
            </div>
          </div>
        </TitleTextCard>

      </div>

    </div>
  );
};

export default OrderReferralPage;
